import { IExpenseListRow } from 'core/hooks';

/**
 * Client-side filter + sort for the Expenses register. The hook loads the WHOLE
 * period set into memory, so every filter/search/sort here is instant — no
 * refetch. Only the date range is a backend filter.
 *
 * NOTE: `status`/`source`/`recurrence` arrive as the graphene UPPER-CASE enum
 * off codegen (`'PAID'`, `'MANUAL'`…), so we lower-case before comparing.
 */

export type TStatusSeg = 'all' | 'toPay' | 'paid' | 'cancelled';

export interface IExpensesView {
  /** Payment segment — `toPay` = planned + confirmed (what still needs paying). */
  statusSeg: TStatusSeg;
  source: string; // '' | 'fakturownia' | 'manual'
  categorySlug: string; // '' | <slug>
  recurrence: string; // '' | 'none' | 'weekly' | 'monthly'
  /** '' = any · 'none' = «Без группы» · otherwise a group relay id. */
  groupId: string;
  search: string;
  /** Show only Fakturownia corrective invoices (faktura korygująca). */
  correctionsOnly: boolean;
}

export type TSortKey =
  | 'date'
  | 'amount'
  | 'status'
  | 'group'
  | 'counterparty'
  | 'category';

export interface IExpensesSort {
  key: TSortKey;
  dir: 'asc' | 'desc';
}

export const DEFAULT_VIEW: IExpensesView = {
  statusSeg: 'all',
  source: '',
  categorySlug: '',
  recurrence: '',
  groupId: '',
  search: '',
  correctionsOnly: false,
};

export const DEFAULT_SORT: IExpensesSort = { key: 'date', dir: 'desc' };

const norm = (s?: string | null): string => (s || '').toLowerCase();

const isToPay = (status?: string | null): boolean => {
  const st = norm(status);
  return st === 'planned' || st === 'confirmed';
};

const matchesSeg = (
  status: string | null | undefined,
  seg: TStatusSeg,
): boolean => {
  if (seg === 'all') return true;
  if (seg === 'toPay') return isToPay(status);
  return norm(status) === seg; // 'paid' | 'cancelled'
};

/** Apply the active view (segment, source, category, recurrence, group, search). */
export const filterExpenses = (
  rows: IExpenseListRow[],
  view: IExpensesView,
): IExpenseListRow[] => {
  const q = view.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (!matchesSeg(r.status, view.statusSeg)) return false;
    if (view.correctionsOnly && !r.isCorrection) return false;
    if (view.source && norm(r.source) !== norm(view.source)) return false;
    if (view.categorySlug && r.category?.slug !== view.categorySlug)
      return false;
    if (view.recurrence && norm(r.recurrence) !== norm(view.recurrence)) {
      return false;
    }
    if (view.groupId === 'none' && r.relatedGroup) return false;
    if (
      view.groupId &&
      view.groupId !== 'none' &&
      r.relatedGroup?.id !== view.groupId
    ) {
      return false;
    }
    if (q) {
      const hay = [
        r.description,
        r.counterpartyName,
        r.relatedGroup?.name,
        r.category?.name,
      ]
        .map(norm)
        .join(' ');
      if (!hay.includes(q)) return false;
    }
    return true;
  });
};

const cmpStr = (a?: string | null, b?: string | null): number =>
  norm(a).localeCompare(norm(b));

/** Stable-ish sort by the chosen column + direction. */
export const sortExpenses = (
  rows: IExpenseListRow[],
  sort: IExpensesSort,
): IExpenseListRow[] => {
  const dir = sort.dir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    let r = 0;
    switch (sort.key) {
      case 'amount':
        r = (a.amountPln ?? 0) - (b.amountPln ?? 0);
        break;
      case 'status':
        r = cmpStr(a.status, b.status);
        break;
      case 'group':
        r = cmpStr(a.relatedGroup?.name, b.relatedGroup?.name);
        break;
      case 'counterparty':
        r = cmpStr(a.counterpartyName, b.counterpartyName);
        break;
      case 'category':
        r = cmpStr(a.category?.name, b.category?.name);
        break;
      case 'date':
      default:
        r = cmpStr(a.date, b.date);
        break;
    }
    // Tie-break on date so equal keys keep a deterministic order.
    if (r === 0 && sort.key !== 'date') r = cmpStr(a.date, b.date);
    return r * dir;
  });
};

export interface IGroupOption {
  id: string;
  name: string;
}

/** The groups actually present in the loaded period — drives the group filter
 *  with only relevant options (no extra query). */
export const groupOptionsFromRows = (
  rows: IExpenseListRow[],
): IGroupOption[] => {
  const byId = new Map<string, string>();
  rows.forEach((r) => {
    if (r.relatedGroup) byId.set(r.relatedGroup.id, r.relatedGroup.name);
  });
  return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

export interface IExpenseKpis {
  /** planned + confirmed + paid (cancelled excluded). */
  total: number;
  /** planned + confirmed — what still needs to be paid for the period. */
  toPay: number;
  paid: number;
  /** rows with no FX rate (amountPln null) — excluded from the sums. */
  missing: number;
}

/** Period totals (over the whole loaded set, before the view filter). */
export const computeKpis = (rows: IExpenseListRow[]): IExpenseKpis => {
  let total = 0;
  let toPay = 0;
  let paid = 0;
  let missing = 0;
  rows.forEach((r) => {
    const v = r.amountPln;
    const st = norm(r.status);
    if (v === null || v === undefined) {
      missing += 1;
      return;
    }
    if (st === 'cancelled') return;
    total += v;
    if (st === 'paid') paid += v;
    else toPay += v; // planned + confirmed
  });
  return { total, toPay, paid, missing };
};
